import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { OrderNavProvider } from "@/context/OrderNavContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { PartnerProvider, usePartner } from "@/context/PartnerContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";

import OrderBuilder from "@/pages/OrderBuilder";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Reorder from "@/pages/Reorder";
import Checkout from "@/pages/Checkout";
import OrderStatus from "@/pages/OrderStatus";
import PayGroup from "@/pages/PayGroup";
import Login from "@/pages/Login";
import TrackOrder from "@/pages/TrackOrder";
import TradeDashboard from "@/pages/TradeDashboard";

import ShippingInfo from "@/pages/ShippingInfo";
import ProductInfo from "@/pages/ProductInfo";
import HowToOrder from "@/pages/HowToOrder";
import Admin from "@/pages/Admin";
import PgpKey from "@/pages/PgpKey";
import EncryptAddress from "@/pages/EncryptAddress";

function StoreLayout() {
  return (
    <div className="App relative">
      <div className="grain" />
      <AnnouncementBar />
      <Navbar />
      <main className="relative z-10 min-h-[70vh]">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Staff doors never exist on a partner's white-label hostname.
function StaffOnly({ children }) {
  const { hostPartner } = usePartner();
  return hostPartner ? <Navigate to="/" replace /> : children;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <OrderNavProvider>
          <SiteContentProvider>
          <PartnerProvider>
          <BrowserRouter>
            <ScrollToTop />
          <Routes>
            <Route element={<StoreLayout />}>
              <Route path="/" element={<OrderBuilder />} />
              <Route path="/shop" element={<OrderBuilder />} />
              <Route path="/build-your-box" element={<OrderBuilder key="build-your-box" initialMode="build" />} />
              <Route path="/multipack-deals" element={<OrderBuilder key="multipack-deals" initialMode="deals" />} />
              <Route path="/guest-collection" element={<OrderBuilder key="guest-collection" initialMode="guest" />} />
              <Route path="/economy-flavours" element={<OrderBuilder key="economy-flavours" initialMode="economy" />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order/:id" element={<OrderStatus />} />
              <Route path="/reorder/:id" element={<Reorder />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/trade" element={<StaffOnly><ProtectedRoute role="dropship"><TradeDashboard /></ProtectedRoute></StaffOnly>} />
              <Route path="/TreeTopper" element={<StaffOnly><Login portal="admin" /></StaffOnly>} />
              <Route path="/TradeAccount" element={<StaffOnly><Login portal="trade" /></StaffOnly>} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/register" element={<Navigate to="/" replace />} />
              <Route path="/wholesale" element={<Navigate to="/" replace />} />
              <Route path="/product-info" element={<ProductInfo />} />
              <Route path="/how-to-order" element={<HowToOrder />} />
              <Route path="/custom-order" element={<Navigate to="/multipack-deals" replace />} />
              <Route path="/shipping" element={<ShippingInfo />} />
              <Route path="/p/:slug" element={<Navigate to="/" replace />} />
              <Route path="/pay/:id" element={<PayGroup />} />
              <Route path="/pgp-key" element={<PgpKey />} />
              <Route path="/encrypt" element={<EncryptAddress />} />
            </Route>
            <Route path="/admin" element={<StaffOnly><ProtectedRoute role="admin"><Admin /></ProtectedRoute></StaffOnly>} />
          </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
          </PartnerProvider>
          </SiteContentProvider>
        </OrderNavProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
